// @ts-strict-ignore
import { useCallback, useEffect, useState } from "react";

import { NoStyleButton, type NoStyleButtonProps } from "@aviary/components/Button";

import * as styles from "./BarButton.styles";

interface Props extends NoStyleButtonProps {
  /**
   * Whether the autoplay is enabled
   * @default false
   */
  isAutoplay?: boolean;
  /**
   * Whether the button is selected
   * @default false
   */
  isSelected?: boolean;
}

const BarButton = ({ isAutoplay = false, isSelected = false, ...rest }: Props) => {
  const [currentValue, setCurrentValue] = useState(0);
  const isAutoplayCountdown = isSelected && isAutoplay;
  const autoplayDuration = 4000; // default duration for Embla autoplay

  const barStyles = [
    styles.progressBar.base,
    isAutoplay && styles.progressBar.firefox(currentValue, autoplayDuration),
    isSelected && !isAutoplay && styles.progressBar.selected,
  ];

  const increment = useCallback(() => {
    if (currentValue > autoplayDuration) {
      setCurrentValue(0);
    } else {
      setCurrentValue(progress => {
        return progress + 115;
      });
    }
  }, [setCurrentValue, currentValue]);

  useEffect(() => {
    if (isAutoplayCountdown) {
      const interval = setInterval(() => {
        increment();
      }, 100);

      return () => {
        clearInterval(interval);
        setCurrentValue(0);
      };
    }
  }, [increment && isAutoplayCountdown]);

  const renderBar = () => {
    if (isAutoplay) {
      return <progress value={currentValue} max={autoplayDuration} css={barStyles} />;
    }

    return <div css={barStyles} data-testid="static-bar" />;
  };

  return (
    <NoStyleButton css={styles.button} {...rest}>
      {renderBar()}
    </NoStyleButton>
  );
};

BarButton.displayName = "BarNavigation.Button";

export { BarButton };
